import type { AxiosError } from 'axios';

/**
 * Shapes returned by the patient self-service API (backend
 * routes/dtp/billing.ts, notificationPrefs.ts, labOrders.ts).
 */

export interface PatientCard {
  /** Opaque handle; the processor's payment-method id never reaches the portal. */
  id: string;
  brand: string;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
  /** A membership bills this card, so it can't be removed here. */
  usedByMembership: boolean;
}

export interface PatientCards {
  paymentMethods: PatientCard[];
  /** False when there is no email on file (the processor customer needs one). */
  canAddCard: boolean;
}

export interface ReceiptSummary {
  id: string;
  kind: 'order' | 'membership';
  paidAt: string;
  description: string;
  orderNumber: string;
  invoiceNumber: string | null;
  amountPaid: number;
  card: { brand: string | null; last4: string } | null;
  refund: { status: 'refunded' | 'partially_refunded'; amount: number } | null;
}

export interface ReceiptDetail extends ReceiptSummary {
  items: { productId: string; productName: string; quantity: number; unitPrice: number; totalPrice: number }[];
  subtotal: number;
  shippingAmount: number;
  visitFee: number;
  total: number;
  patientName: string;
  clinicName: string | null;
  clinicPhone: string | null;
  statementName: string;
  canEmail: boolean;
}

export type NotificationCategory = 'orderUpdates' | 'labResults' | 'careMessages' | 'refillReminders';
export type NotificationChannel = 'sms' | 'email';
export type NotificationPrefs = Record<NotificationCategory, Record<NotificationChannel, boolean>>;

export interface NotificationSettings {
  prefs: NotificationPrefs;
  hasEmail: boolean;
  sms: {
    phoneLast4: string | null;
    canText: boolean;
    optedOut: boolean;
    /** stop: replied STOP (text START to resume); web: switched off here; clinic: set by the clinic. */
    optedOutBy: 'stop' | 'web' | 'clinic' | null;
  };
}

export type LabRequisitionType = 'at_home_kit' | 'walk_in' | 'mobile_phleb';

export interface PatientLabOrder {
  id: string;
  panelName: string;
  requisitionType: LabRequisitionType;
  orderNumber: string | null;
  orderedAt: string;
  fasting: boolean;
  state: 'in_progress' | 'results_ready' | 'needs_attention' | 'cancelled';
  steps: { label: string; reached: boolean; at: string | null }[];
  currentStep: number;
  nextStep: string | null;
  turnaroundDays: number | null;
  resultId: string | null;
}

/** The server's own message for a refused request, or a fallback. */
export function selfServiceError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const data = (err as AxiosError<{ error?: string }>)?.response?.data;
  return (data && typeof data.error === 'string' && data.error) || fallback;
}

export { formatMoney } from './format';

export function formatDate(value?: string | null, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, opts);
}

const BRANDS: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', americanexpress: 'American Express', discover: 'Discover', jcb: 'JCB', dinersclub: 'Diners Club', unionpay: 'UnionPay' };

export function cardBrandLabel(brand?: string | null) {
  if (!brand) return 'Card';
  const key = brand.toLowerCase().replace(/[^a-z]/g, '');
  return BRANDS[key] || brand.charAt(0).toUpperCase() + brand.slice(1);
}
